import { fetchPreviousMeetingsData } from "."
import { setCurrentPage } from "../../reducers/navigation"
import { meetingTransactionsLoaded } from "../getTransactions"
import { fetchMeeting } from "./getMeeting"
import { backendAPI } from "../../api"
import { v4 as uuidv4 } from "uuid"
import { safeApiCall } from "../../api/offline"
import { MEETING_ADDED_LOCALLY, MEETING_CREATED_SUCCESS, MEETING_ONGOING_CLEAR, MEETING_SYNC_FAILED } from "../../reducers/base/meeting"
import { triggerAction } from "../../constants"
import { decryptObject, deleteCacheByKey, getCacheById } from "../../../view/shared/utilities/cache"
import { loadLocalData } from "../getVslas"

export const isCreatingMeeting = (bool) => ({
  type: "IS_CREATING_MEETING",
  isCreating: bool,
})

export const createMeeting = (meeting) => ({
  type: "CREATE_MEETING",
  meeting,
})

export const createMeetingError = (error) => ({
  type: "CREATE_MEETING_ERROR",
  error: error,
})

export function postCreateMeeting(payload) {
  return async function (dispatch, getState) {
    // dispatch(isCreatingMeeting(true))
    try {
      const { group, user } = getState()
      const groupId = group?.id
      const isTempGroup = groupId ? false : true;
      const tempId = payload.tempId || uuidv4()
      const data = { ...payload, request_id: tempId, groupId: groupId || null }
      const urlPart = `/groups-hedera/${isTempGroup ? "{{group}}" : groupId}/meetings?requestId=${tempId}`
      const cachedUrl = `${window.REACT_APP_PWA_APP_DOMAIN}${urlPart}`
      const headers = {
        Authorization: `Bearer ${user.token}`
      };
      const optimisticMeeting = {
        ...data,
        cacheId: tempId,
        is_offline: isTempGroup
      }
      dispatch(triggerAction("SET_ACTIVE_MEETING_TRANSACTIONS", []))
      const response = await safeApiCall(
        {
          // --- Core Request Config ---
          method: 'POST',
          url: cachedUrl, // Include the tempId in the URL for tracking
          data: {
            ...data,
            is_offline: isTempGroup
          },
          headers: headers,

          // --- Offline & Queueing Metadata ---
          tempId: tempId, // Using the cacheId from the 'groups' table
          entityType: 'meeting',
          dependsOn: isTempGroup ? 'group' : null,
          needBodyUpdate: isTempGroup ? { groupId: 'group' } : null,
          userId: user.id, // Pass the userId for cache updates

          // --- Redux Actions for automatic dispatch ---
          optimisticActions: [
            data.status === 'completed' ? {
              type: MEETING_ONGOING_CLEAR, payload: { tempId }
            } :
              { type: MEETING_ADDED_LOCALLY, payload: optimisticMeeting },
          ],
          successAction: [
            { type: MEETING_CREATED_SUCCESS },
            { type: MEETING_ONGOING_CLEAR, payload: { tempId } },
          ],
          failAction: { type: MEETING_SYNC_FAILED }
        },
        // The actual API call callback
        async () => backendAPI.post(urlPart, data)
      );

      if (response && !response?.data.queued) {
        dispatch(triggerAction(MEETING_CREATED_SUCCESS, {
          ...response.data,
          tempId: tempId
        }));
        await dispatch(fetchMeeting())
      }
      return Promise.resolve()
    } catch (error) {
      console.error("Error creating meeting:", error);
      if (error) {
        dispatch(createMeetingError(error))
      }
      return Promise.reject()
    }
    finally {
      dispatch(isCreatingMeeting(false))
    }
  }
}

export function patchMeeting(data) {
  return async function (dispatch, getState) {
    dispatch(isCreatingMeeting(true))
    try {
      const { meeting, user, isConnected } = getState()
      const isTempMeeting = !meeting.ongoing?.id
      let tempId = meeting.ongoing?.cacheId
      const headers = {
        Authorization:
          `Bearer ${user.token}`,
      }

      if (data.status === "completed") {
        dispatch(triggerAction("SET_ACTIVE_MEETING_TRANSACTIONS", []))
      }

      if (isTempMeeting) {
        // recreate the meeting with the correct data
        let cachedMeeting = await getCacheById('offlineRequests', tempId)
        if (!cachedMeeting?.length) {
          console.error("Cached meeting not found for tempId:", tempId);
          return Promise.reject(new Error("Cached meeting not found"));
        }
        cachedMeeting = cachedMeeting[0];
        const decryptedString = decryptObject(cachedMeeting.encryptedBody, cachedMeeting.salt);
        let decryptedBody = JSON.parse(decryptedString);
        const updatedPayload = {
          ...decryptedBody.body,
          status: 'completed',
          tempId: tempId,
        }
        await deleteCacheByKey("offlineRequests", "cacheId", tempId)
        dispatch(postCreateMeeting(updatedPayload))
        dispatch(triggerAction(MEETING_ONGOING_CLEAR, { tempId }));
        dispatch(loadLocalData())
      }
      else {
        tempId = tempId || uuidv4() // Generate a new tempId if not a temp meeting
        const urlPart = `/meetings/${meeting.ongoing.id}`
        const cachedUrl = `${window.REACT_APP_PWA_APP_DOMAIN}${urlPart}`

        const response = await safeApiCall(
          {
            // --- Core Request Config ---
            method: 'PATCH',
            data: {
              ...data,
              request_id: tempId, // Ensure the requestId is included
            },
            headers,
            url: cachedUrl,

            // --- Offline & Queueing Metadata ---
            tempId: tempId, // Use the ongoing meeting's cacheId
            entityType: 'meeting',
            dependsOn: null,
            userId: user.id, // Pass the userId for cache updates

            // --- Redux Actions for automatic dispatch ---
            optimisticActions: [
              { type: MEETING_ONGOING_CLEAR, payload: { tempId } },
            ],
            successAction: [
              { type: MEETING_ONGOING_CLEAR, payload: { tempId } },

            ],
            failAction: { type: MEETING_SYNC_FAILED }
          }, async () => backendAPI.patch(urlPart, data))
        if (response && !response.data?.queued) {
          console.log(`[Action] Meeting '${data.date}' ${meeting?.ongoing.id} updated successfully online.`)
          dispatch(setCurrentPage("group-meetingsMenu"))
          dispatch(fetchPreviousMeetingsData())
          dispatch(fetchMeeting())
        }
      }
      dispatch(triggerAction(MEETING_ONGOING_CLEAR, { tempId, id: meeting.ongoing?.id }));

      return Promise.resolve()
    } catch (error) {
      return dispatch(createMeetingError(error))
    }
    finally {
      dispatch(isCreatingMeeting(false))
    }
  }
}
